import * as React from 'react';
import { useBudgets } from '@/hooks/useBudgets';
import { useBudgetAnalytics } from '@/hooks/useBudgetAnalytics';
import { useFxRateUtils } from '@/hooks/useFxRates';
import { filterRootBudgets } from '@/pages/dashboardBudgetFilters';
import {
  collectCurrencyOptions,
  mergeSeriesForChart,
} from '@/pages/analytics/budgetAnalyticsUtils';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingCard } from '@/components/ui/Loading';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Page } from '@/components/layout/Page';
import type { Budget } from '@/types';

interface TooltipPayloadEntry {
  dataKey: string;
  value: number;
  color: string;
  name: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  series: ReturnType<typeof useBudgetAnalytics>['series'];
  displayCurrency: string;
}

function CustomTooltip({ active, payload, label, series, displayCurrency }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 max-w-xs">
      <p className="text-sm font-medium text-foreground mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry) => {
          if (entry.value == null) return null;
          const budgetSeries = series.find((s) => s.budgetId === entry.dataKey);
          return (
            <p key={entry.dataKey} className="text-sm text-muted-foreground">
              <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ backgroundColor: entry.color }} />
              {entry.name}:{' '}
              <span className="font-medium text-foreground">
                {formatCurrency(entry.value, displayCurrency)}
              </span>
              {budgetSeries && budgetSeries.originalCurrency !== displayCurrency && (
                <span className="block text-xs mt-0.5 pl-3.5">
                  Period: {formatDate(budgetSeries.periodLabel.split(' – ')[0]!, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
}

function BudgetCheckbox({
  budget,
  checked,
  onChange,
}: {
  budget: Budget;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
        checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
      )}
    >
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground truncate">{budget.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatDate(budget.startDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          {' – '}
          {formatDate(budget.endDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          {' · '}
          {formatCurrency(budget.amount, budget.currency)}
        </p>
      </div>
    </label>
  );
}

export function BudgetAnalyticsPage() {
  const { data: budgets = [], isLoading: budgetsLoading } = useBudgets();
  const { getCommonCurrencies } = useFxRateUtils();

  const rootBudgets = React.useMemo(() => filterRootBudgets(budgets), [budgets]);

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [displayCurrencyChoice, setDisplayCurrencyChoice] = React.useState('AUTO');
  const [clipStart, setClipStart] = React.useState('');
  const [clipEnd, setClipEnd] = React.useState('');

  React.useEffect(() => {
    if (rootBudgets.length > 0 && selectedIds.size === 0) {
      setSelectedIds(new Set([rootBudgets[0]!.id]));
    }
  }, [rootBudgets, selectedIds.size]);

  const selectedBudgets = React.useMemo(
    () => rootBudgets.filter((b) => selectedIds.has(b.id)),
    [rootBudgets, selectedIds]
  );

  const currencyOptions = React.useMemo(
    () => collectCurrencyOptions(rootBudgets, getCommonCurrencies()),
    [rootBudgets, getCommonCurrencies]
  );

  const { series, displayCurrency, currencyWarning, isLoading, isError } = useBudgetAnalytics(
    selectedBudgets,
    displayCurrencyChoice,
    clipStart || undefined,
    clipEnd || undefined
  );

  const chartData = React.useMemo(() => mergeSeriesForChart(series), [series]);

  const toggleBudget = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(rootBudgets.map((b) => b.id)));
  const clearAll = () => setSelectedIds(new Set());

  return (
    <Page className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Budget Analytics</h1>
        <p className="text-sm text-muted-foreground">
          Compare spending across budgets and months. Lines are aligned by day of period.
        </p>
      </div>

      {budgetsLoading ? (
        <LoadingCard className="min-h-[200px]" />
      ) : rootBudgets.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground">No budgets found.</CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">Compare budgets</p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                    Select all
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={clearAll}>
                    Clear
                  </Button>
                </div>
              </div>

              <div className="grid gap-2 max-h-56 overflow-y-auto sm:grid-cols-2">
                {rootBudgets.map((budget) => (
                  <BudgetCheckbox
                    key={budget.id}
                    budget={budget}
                    checked={selectedIds.has(budget.id)}
                    onChange={(checked) => toggleBudget(budget.id, checked)}
                  />
                ))}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Select
                  id="display-currency"
                  label="Display currency"
                  value={displayCurrencyChoice}
                  onChange={(e) => setDisplayCurrencyChoice(e.target.value)}
                  options={[
                    { value: 'AUTO', label: 'Auto (budget currency or USD)' },
                    ...currencyOptions.map((c) => ({ value: c, label: c })),
                  ]}
                  helperText="Amounts use FX rates on each transaction date"
                />
                <Input
                  type="date"
                  label="Filter from (optional)"
                  value={clipStart}
                  onChange={(e) => setClipStart(e.target.value)}
                />
                <Input
                  type="date"
                  label="Filter to (optional)"
                  value={clipEnd}
                  onChange={(e) => setClipEnd(e.target.value)}
                />
              </div>

              {currencyWarning && (
                <p className="text-sm text-warning">{currencyWarning}</p>
              )}
            </CardContent>
          </Card>

          {selectedBudgets.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-muted-foreground">
                Select at least one budget to compare.
              </CardContent>
            </Card>
          ) : isLoading ? (
            <LoadingCard className="min-h-[200px]" />
          ) : isError ? (
            <Card>
              <CardContent className="p-6 text-destructive">
                Failed to load analytics. Check your connection and try again.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-4">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      stroke="currentColor"
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      stroke="currentColor"
                      tickFormatter={(value) => formatCurrency(value, displayCurrency)}
                      width={88}
                    />
                    <Tooltip
                      content={
                        <CustomTooltip series={series} displayCurrency={displayCurrency} />
                      }
                    />
                    <Legend verticalAlign="bottom" height={48} iconType="line" />
                    {series.map((s) => (
                      <Line
                        key={s.budgetId}
                        type="monotone"
                        dataKey={s.budgetId}
                        name={`${s.budgetName} (${s.originalCurrency})`}
                        stroke={s.color}
                        strokeWidth={2.5}
                        dot={false}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>

                <div className="grid gap-3 sm:grid-cols-2">
                  {series.map((s) => (
                    <div
                      key={s.budgetId}
                      className="rounded-lg border border-border p-4 space-y-2"
                      style={{ borderLeftWidth: 4, borderLeftColor: s.color }}
                    >
                      <p className="font-medium text-foreground">{s.budgetName}</p>
                      <p className="text-xs text-muted-foreground">{s.periodLabel}</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Budget</p>
                          <p className="font-medium">
                            {formatCurrency(s.budgetAmount, displayCurrency)}
                          </p>
                          {s.originalCurrency !== displayCurrency && (
                            <p className="text-xs text-muted-foreground">
                              {formatCurrency(s.originalBudgetAmount, s.originalCurrency)}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground">Spent</p>
                          <p className="font-medium">
                            {formatCurrency(s.totalSpent, displayCurrency)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Remaining</p>
                          <p
                            className={cn(
                              'font-medium',
                              s.remaining < 0 ? 'text-destructive' : 'text-foreground'
                            )}
                          >
                            {formatCurrency(s.remaining, displayCurrency)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Days</p>
                          <p className="font-medium">{s.remainingByDay.length}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Page>
  );
}
